/**
 * Results Tab - Display scheduling algorithm results
 *
 * Shows:
 * - Button to run scheduling algorithm
 * - Scheduling statistics (total, scheduled, unscheduled, rate)
 * - Summary of scheduled classes
 * - List of unscheduled classes with reasons
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  LinearProgress,
  Divider,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import {
  useRunScheduleMutation,
  useGetScheduledClassesQuery,
  useGetClassesQuery,
} from '../../../api/schedulingApi';
import type { RunScheduleResponse } from '../../../types/scheduling';

interface ResultsTabProps {
  scheduleId: string;
  studioId: string;
}

const ResultsTab: React.FC<ResultsTabProps> = ({ scheduleId, studioId }) => {
  const [runSchedule, { isLoading: isRunning }] = useRunScheduleMutation();
  const [lastRunResults, setLastRunResults] =
    useState<RunScheduleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch scheduled classes to show summary
  const {
    data: scheduledClasses,
    isLoading: isLoadingScheduled,
    refetch: refetchScheduled,
  } = useGetScheduledClassesQuery(
    { scheduleId, studioId },
    { skip: !scheduleId || !studioId }
  );

  // Fetch all classes for the schedule to calculate statistics
  const { data: allClasses } = useGetClassesQuery(
    { scheduleId, studioId },
    { skip: !scheduleId || !studioId }
  );

  const handleRunSchedule = async () => {
    try {
      setError(null);
      const response = await runSchedule({ id: scheduleId, studioId }).unwrap();
      setLastRunResults(response);
      // Refetch scheduled classes to show updated data
      await refetchScheduled();
    } catch (err: any) {
      console.error('Failed to run schedule:', err);
      setError(
        err?.data?.detail ||
          'Failed to run scheduling algorithm. Please try again.'
      );
      setLastRunResults(null);
    }
  };

  // Calculate statistics from scheduled classes and all classes
  const results = React.useMemo(() => {
    // If we just ran the algorithm, use those results
    if (lastRunResults) {
      return lastRunResults;
    }

    // Otherwise, calculate statistics from persisted data
    if (scheduledClasses && allClasses) {
      const totalClasses = allClasses.length;
      const scheduledCount = scheduledClasses.length;
      const unscheduledCount = totalClasses - scheduledCount;
      const schedulingRate =
        totalClasses > 0 ? scheduledCount / totalClasses : 0;

      return {
        status: 'success',
        stats: {
          total_classes: totalClasses,
          scheduled_classes: scheduledCount,
          unscheduled_classes: unscheduledCount,
          scheduling_rate: schedulingRate,
          unscheduled_by_room: 0, // Can't determine from persisted data
          unscheduled_by_teacher: 0, // Can't determine from persisted data
        },
        unscheduled_classes: [], // Can't determine from persisted data
      };
    }

    return null;
  }, [lastRunResults, scheduledClasses, allClasses]);

  const formatSchedulingRate = (rate: number): string =>
    `${(rate * 100).toFixed(1)}%`;

  const getSchedulingRateColor = (rate: number): string => {
    if (rate >= 0.9) return 'success.main';
    if (rate >= 0.7) return 'warning.main';
    return 'error.main';
  };

  return (
    <Box>
      {/* Header with Run Button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">Scheduling Algorithm Results</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            isRunning ? <CircularProgress size={20} /> : <PlayArrowIcon />
          }
          onClick={handleRunSchedule}
          disabled={isRunning}
        >
          {isRunning ? 'Running Algorithm...' : 'Run Scheduling Algorithm'}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Instructions (before running) */}
      {!results && !error && !isRunning && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Ready to schedule classes</strong>
          </Typography>
          <Typography variant="body2">
            Click &ldquo;Run Scheduling Algorithm&rdquo; to automatically assign
            classes to rooms and teachers based on availability and preferences.
            The algorithm will:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0 }}>
            <li>
              <Typography variant="body2">
                Assign classes to available room-time slots
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Assign teachers based on availability and specializations
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Respect scheduling preferences when possible
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Balance class distribution across rooms and days
              </Typography>
            </li>
          </Box>
        </Alert>
      )}

      {/* Results Display */}
      {results && (
        <>
          {/* Statistics Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 3,
              mb: 4,
            }}
          >
            {/* Total Classes */}
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Classes
                </Typography>
                <Typography variant="h3" component="div">
                  {results.stats.total_classes}
                </Typography>
              </CardContent>
            </Card>

            {/* Scheduled Classes */}
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Scheduled
                </Typography>
                <Typography variant="h3" component="div" color="success.main">
                  {results.stats.scheduled_classes}
                </Typography>
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Success"
                  color="success"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>

            {/* Unscheduled Classes */}
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Unscheduled
                </Typography>
                <Typography variant="h3" component="div" color="error.main">
                  {results.stats.unscheduled_classes}
                </Typography>
                {results.stats.unscheduled_classes > 0 && (
                  <Chip
                    icon={<WarningIcon />}
                    label="Review Needed"
                    color="warning"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>

            {/* Success Rate */}
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Success Rate
                </Typography>
                <Typography
                  variant="h3"
                  component="div"
                  sx={{
                    color: getSchedulingRateColor(
                      results.stats.scheduling_rate
                    ),
                  }}
                >
                  {formatSchedulingRate(results.stats.scheduling_rate)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={results.stats.scheduling_rate * 100}
                  sx={{ mt: 1 }}
                  color={(() => {
                    if (results.stats.scheduling_rate >= 0.9) return 'success';
                    if (results.stats.scheduling_rate >= 0.7) return 'warning';
                    return 'error';
                  })()}
                />
              </CardContent>
            </Card>
          </Box>

          {/* Breakdown Section */}
          {results.stats.unscheduled_classes > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Unscheduled Classes Breakdown
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <ErrorIcon color="error" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        No Room Available
                      </Typography>
                      <Typography variant="h6">
                        {results.stats.unscheduled_by_room}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <WarningIcon color="warning" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        No Teacher Available
                      </Typography>
                      <Typography variant="h6">
                        {results.stats.unscheduled_by_teacher}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Unscheduled Classes Table */}
          {results.unscheduled_classes.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Unscheduled Classes ({results.unscheduled_classes.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Class Name</TableCell>
                      <TableCell>Style(s)</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Age Range</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.unscheduled_classes.map(cls => (
                      <TableRow key={cls.class_id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {cls.class_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {cls.styles.map(style => (
                              <Chip key={style} label={style} size="small" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>{cls.level}</TableCell>
                        <TableCell>
                          {cls.age_start}-{cls.age_end}
                        </TableCell>
                        <TableCell>{cls.duration}h</TableCell>
                        <TableCell>
                          <Chip
                            label={cls.reason}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Success Message */}
          {results.stats.unscheduled_classes === 0 && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="body1" gutterBottom>
                <strong>Perfect Schedule!</strong>
              </Typography>
              <Typography variant="body2">
                All {results.stats.total_classes} classes have been successfully
                scheduled. View the Schedule Grid tab to see the complete
                schedule.
              </Typography>
            </Alert>
          )}

          {/* Next Steps */}
          {results.stats.scheduled_classes > 0 && (
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Next Steps:</strong>
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                <li>
                  <Typography variant="body2">
                    View the <strong>Schedule Grid</strong> tab to see the
                    visual schedule
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Make manual adjustments by dragging classes to different
                    times/rooms
                  </Typography>
                </li>
                {results.stats.unscheduled_classes > 0 && (
                  <li>
                    <Typography variant="body2">
                      Review unscheduled classes and add more room/teacher
                      availability if needed
                    </Typography>
                  </li>
                )}
                <li>
                  <Typography variant="body2">
                    Re-run the algorithm after making changes to preferences or
                    availability
                  </Typography>
                </li>
              </Box>
            </Alert>
          )}
        </>
      )}

      {/* Scheduled Classes Summary (always show if data exists) */}
      {scheduledClasses && scheduledClasses.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Current Schedule Summary
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Class</TableCell>
                  <TableCell>Day</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Teacher(s)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoadingScheduled ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  scheduledClasses
                    .slice(0, 10) // Show first 10 for preview
                    .map(sc => (
                      <TableRow key={sc.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {sc.class_details.class_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sc.class_details.level} •{' '}
                            {sc.class_details.age_start}-
                            {sc.class_details.age_end}
                          </Typography>
                        </TableCell>
                        <TableCell>{sc.day}</TableCell>
                        <TableCell>
                          {sc.start_time} - {sc.end_time}
                        </TableCell>
                        <TableCell>{sc.room_details.room_name}</TableCell>
                        <TableCell>
                          {sc.teacher_details &&
                          sc.teacher_details.length > 0 ? (
                            sc.teacher_details
                              .map(t => t.teacher_name)
                              .join(', ')
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              None assigned
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {scheduledClasses.length > 10 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, ml: 1 }}
            >
              Showing 10 of {scheduledClasses.length} scheduled classes. View
              all in the Schedule Grid tab.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ResultsTab;
